import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { getAllGroups, updateGroupRoles } from '../../../../services/groups';

const formatGroupPeriod = (group) => {
    const start = dayjs(group?.start_date);
    const end = dayjs(group?.end_date);

    if (!start.isValid() && !end.isValid()) return null;
    if (!start.isValid()) return end.format('DD/MM/YYYY');
    if (!end.isValid()) return start.format('DD/MM/YYYY');
    return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
};

const AssignPatientGroupDialog = ({
    open,
    onClose,
    patientUuid,
    patientName,
    onAssigned
}) => {
    const { t } = useTranslation();
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);
    const [assignmentError, setAssignmentError] = useState(false);

    const {
        data: groups = [],
        isPending: groupsLoading,
        isError: groupsError
    } = useQuery({
        queryKey: ['patient-group-assignment', 'active-groups'],
        queryFn: () => getAllGroups(true),
        enabled: open,
        staleTime: 0,
        refetchOnMount: 'always'
    });

    useEffect(() => {
        if (open) return;
        setSelectedGroup(null);
        setAssignmentError(false);
    }, [open]);

    const handleClose = () => {
        if (!isAssigning) onClose();
    };

    const handleAssign = async () => {
        if (!selectedGroup) return;

        setIsAssigning(true);
        setAssignmentError(false);

        try {
            await updateGroupRoles({
                uuid: selectedGroup.uuid,
                to_add: [patientUuid],
                to_remove: []
            });
            await onAssigned();
            onClose();
        } catch {
            setAssignmentError(true);
        } finally {
            setIsAssigning(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('userPage.overview.assignments.groupDialogTitle')}
            </DialogTitle>
            <DialogContent>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {t('userPage.overview.assignments.groupDialogDescription', {
                        name: patientName
                    })}
                </Typography>

                {groupsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {t('userPage.overview.assignments.loadGroupsError')}
                    </Alert>
                )}

                <Autocomplete
                    options={groups}
                    value={selectedGroup}
                    onChange={(_, value) => {
                        setSelectedGroup(value);
                        setAssignmentError(false);
                    }}
                    getOptionLabel={(option) => option?.name || ''}
                    isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                    loading={groupsLoading}
                    disabled={groupsError || isAssigning}
                    noOptionsText={t('userPage.overview.assignments.noActiveGroups')}
                    loadingText={t('userPage.overview.assignments.loading')}
                    renderOption={(props, option) => {
                        // React requires the option key to be passed separately from spread props.
                        // eslint-disable-next-line react/prop-types
                        const { key, ...optionProps } = props;
                        return (
                            <Box component="li" key={key} {...optionProps}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography fontWeight={600}>
                                        {option.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {formatGroupPeriod(option)}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('userPage.overview.assignments.selectGroup')}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {groupsLoading ? (
                                            <CircularProgress color="inherit" size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                )
                            }}
                        />
                    )}
                />

                {selectedGroup && (
                    <Stack
                        spacing={0.75}
                        sx={{
                            mt: 2.5,
                            p: 2,
                            border: '1px solid #E0E5E9',
                            borderRadius: 1.5
                        }}
                    >
                        <Typography fontWeight={700}>{selectedGroup.name}</Typography>
                        {selectedGroup.description && (
                            <Typography variant="body2" color="text.secondary">
                                {selectedGroup.description}
                            </Typography>
                        )}
                        <Typography variant="body2" color="primary">
                            {formatGroupPeriod(selectedGroup)}
                        </Typography>
                        {Array.isArray(selectedGroup.patients) && (
                            <Typography variant="body2" color="text.secondary">
                                {t('userPage.overview.assignments.groupMembers', {
                                    count: selectedGroup.patients.length
                                })}
                            </Typography>
                        )}
                    </Stack>
                )}

                {assignmentError && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {t('userPage.overview.assignments.assignGroupError')}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleClose} disabled={isAssigning}>
                    {t('userPage.overview.assignments.cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    disabled={!selectedGroup || isAssigning}
                    startIcon={
                        isAssigning ? (
                            <CircularProgress color="inherit" size={16} />
                        ) : null
                    }
                >
                    {isAssigning
                        ? t('userPage.overview.assignments.assigningGroup')
                        : t('userPage.overview.assignments.assignGroup')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

AssignPatientGroupDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    patientUuid: PropTypes.string.isRequired,
    patientName: PropTypes.string,
    onAssigned: PropTypes.func.isRequired
};

export default AssignPatientGroupDialog;
